import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Exists, Timer} from "components";
import {DateTime} from "luxon";
import {getNotBelowZero, isBoolean} from "modules/utils/helpers";
import styled from "@emotion/styled";
import {useDispatch, useSelector} from "react-redux";
import {
	getCurrentQuestion,
	getCurrentQuestionIndex,
	getIsButtonsDisabled,
	getIsTimerVisible,
	getTimerTime,
} from "modules/selectors";

import guessCorrectAsset from "assets/images/correct.svg";
import guessIncorrectAsset from "assets/images/incorrect.svg";
import guessAwaitAsset from "assets/images/await.svg";
import {CircularProgress} from "@mui/material";
import {
	getFadeInAnimation,
	getFadeOutAnimation,
	rotateAnimationHandler,
} from "assets/images/animation";
import {NullOrEmpty} from "modules/types";
import {answerQuestion, setTimerTime} from "modules/actions";
import countdown from "assets/images/countdown.svg";
import {eq, get} from "lodash";

const Wrapper = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: 20;
	transition-delay: 1s;
	transition: 0.25s;

	&:not(.incorrect):not(.correct) {
		${getFadeInAnimation(500)};
	}

	&.incorrect,
	&.correct {
		${getFadeOutAnimation(1000)};
	}

	&.appear {
		${getFadeInAnimation(1000, true)};
	}
`;

const CircleWrapper = styled.div`
	position: relative;
	z-index: 5;
	width: 60px;
	height: 60px;
	border-radius: 50%;
	margin: 0 auto;

	display: flex;
	justify-content: center;
	align-items: center;

	circle:not(.MuiCircularProgress-circle) {
		transform: rotate(-90deg);
		transition: 1000ms cubic-bezier(0.2, 0.2, 0.2, 0.2);
		transform-origin: 50% 50%;
	}

	.path {
		transform-origin: center;
		transform: rotate(90deg) rotateY(180deg);
		stroke: ${({theme}) => theme.colors.brand.primary};
	}

	&.warning {
		.circle-div {
			background: ${({theme}) => theme.colors.brand.secondaryHover};
		}

		.path {
			stroke: ${({theme}) => theme.colors.brand.secondaryHover};
		}
	}

	.MuiCircularProgress-root {
		z-index: 6;
		position: absolute;
		left: 0;
		top: 0;
		right: 0;
		bottom: 0;
		width: unset !important;
		height: unset !important;

		circle {
			stroke-width: 2px;
		}
	}

	&.incorrect span {
		transition: 0.5s;
		color: #c20000;
		${rotateAnimationHandler};
	}

	&.correct span {
		transition: 0.5s;
		color: #18c971;
		${rotateAnimationHandler};
	}

	&.guess span {
		transition: 0.5s;
		color: ${({theme}) => theme.colors.brand.primary};
		${rotateAnimationHandler};
	}
`;
const Circle = styled.div`
	position: absolute;
	left: 5px;
	top: 5px;
	right: 5px;
	bottom: 5px;
	border-radius: 50%;
	background: ${({theme}) => theme.colors.brand.black};

	display: flex;
	justify-content: center;
	align-items: center;
	color: ${({theme}) => theme.colors.text.white};
	border: 2px solid ${({theme}) => theme.colors.text.white};

	&:after {
		content: "";
		position: absolute;
		right: -4px;
		top: -4px;
		left: -4px;
		bottom: -4px;
		border: 2px solid ${({theme}) => theme.colors.brand.black};
		-webkit-border-radius: 50%;
		-moz-border-radius: 50%;
		border-radius: 50%;
	}

	.renderer-timer-time {
		font-size: 14px;
	}
`;

const Time = styled.p`
	position: absolute;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -70%);
	font-size: 14px;
	color: ${({theme}) => theme.colors.text.white};
`;

const GuessImage = styled.img`
	width: 55px;
	height: 55px;
`;

const getTimerClass = (guess: NullOrEmpty<boolean>, isButtonsDisabled: boolean) => {
	if (guess === true) {
		return "correct";
	}

	if (guess === false) {
		return "incorrect";
	}

	if (isButtonsDisabled) {
		return "guess";
	}

	return "";
};

interface ITimerProps {
	paused: boolean;
}

export const GameTimer: React.FC<ITimerProps> = ({paused}) => {
	const dispatch = useDispatch();
	const [progress, setProgress] = useState(100);
	const [timerClass, setTimerClass] = useState("");
	const questionGuess = useSelector(getCurrentQuestion)?.isCorrect;
	const isTimerVisible = useSelector(getIsTimerVisible);
	const isButtonsDisabled = useSelector(getIsButtonsDisabled);
	const timeToEnd = useSelector(getTimerTime);
	const questionIdx = useSelector(getCurrentQuestionIndex);

	const isFirstQuestion = eq(questionIdx, 1);

	const timerGuessClass = getTimerClass(questionGuess, isButtonsDisabled);

	const question = useSelector(getCurrentQuestion);
	const timeToQuestion = get(question, "timeToQuestion", 10);

	useEffect(() => {
		if (timeToEnd === 0 && isTimerVisible) {
			dispatch(answerQuestion({answer: null, time: 0}));
		}
	}, [isTimerVisible, timeToEnd, timerClass, dispatch]);

	useEffect(() => {
		setProgress(100);
		setTimerClass("");
	}, [isTimerVisible]);

	const date = useMemo(() => {
		if (isTimerVisible || paused) {
			return DateTime.now().plus({second: timeToQuestion}).toISO();
		}

		return DateTime.now().plus({second: timeToQuestion}).toISO();
	}, [isTimerVisible, timeToQuestion, paused]);

	const onTick = useCallback(
		(toEnd: number) => {
			const calculated = toEnd / 100;
			setProgress(getNotBelowZero(calculated));

			const parsedTime = Math.floor((toEnd + 1000) / 1000);
			if (timeToEnd !== parsedTime) {
				dispatch(setTimerTime(parsedTime));
			}

			if (parsedTime < 4 && timerClass !== "warning") {
				setTimerClass("warning");
			}
		},
		[timerClass, timeToEnd, dispatch]
	);

	if (isBoolean(questionGuess) || isButtonsDisabled) {
		return (
			<Wrapper className={timerGuessClass}>
				<CircleWrapper className={timerGuessClass}>
					<CircularProgress variant="determinate" value={50} />
					<Exists when={questionGuess === true}>
						<GuessImage src={guessCorrectAsset} alt="Correct" />
					</Exists>
					<Exists when={questionGuess === false}>
						<GuessImage src={guessIncorrectAsset} alt="Incorrect" />
					</Exists>
					<Exists when={isButtonsDisabled && !isBoolean(questionGuess)}>
						<GuessImage src={guessAwaitAsset} alt="Waiting" />
					</Exists>
				</CircleWrapper>
			</Wrapper>
		);
	}

	if (isFirstQuestion || paused) {
		return (
			<Wrapper className="appear">
				<Time>{timeToQuestion}</Time>
				<img src={countdown} alt="non-started-timer" />
			</Wrapper>
		);
	}

	if (!isTimerVisible) {
		return <Wrapper />;
	}

	return (
		<Wrapper>
			<CircleWrapper className={timerClass}>
				<Circle className="circle-div">
					<Timer date={date} onTick={onTick} />
				</Circle>
				<svg height="100%" width="100%" viewBox="0 0 20 20">
					<circle r="10" cx="10" cy="10" fill="transparent" />
					<circle
						r="5"
						cx="10"
						cy="10"
						fill="transparent"
						className="path"
						stroke="#FFFFFF"
						strokeWidth="10"
						strokeDasharray={`calc(${progress} * 31.4 / 100) 31.4`}
						transform=""
					/>
				</svg>
			</CircleWrapper>
		</Wrapper>
	);
};
